<script lang="ts" setup>
import type { NavLink } from '#core/types'
import type { Component } from 'vue'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@forgd/ui-kit/collapsible'
import { Tabs, TabsIndicator, TabsList, TabsTriggerLink } from '@forgd/ui-kit/tabs'
import {
  ArrowsRightLeftIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  FlagIcon,
  PresentationChartLineIcon,
  Square3Stack3DIcon,
} from '@heroicons/vue/24/outline'

const props = defineProps<{
  items: NavLink[]
  docsItems?: NavLink[]
}>()

const route = useRoute()
const NuxtLink = resolveComponent('NuxtLink')
const isOpen = ref(false)

// `NavLink.icon` is still an Iconify class name; map the ones this nav uses to heroicons.
const navIcons: Record<string, Component> = {
  'i-heroicons-square-3-stack-3d': Square3Stack3DIcon,
  'i-heroicons-chart-bar': ChartBarIcon,
  'i-heroicons-presentation-chart-line': PresentationChartLineIcon,
  'i-heroicons-arrows-right-left': ArrowsRightLeftIcon,
  'i-heroicons-flag': FlagIcon,
  'i-heroicons-document-text': DocumentTextIcon,
}

function navIcon(item: NavLink): Component | undefined {
  return item.icon ? navIcons[item.icon] : undefined
}

function isActive(item: NavLink): boolean {
  if (!item.to) {
    return false
  }
  if (item.shouldHighlight) {
    return item.shouldHighlight(route.path)
  }
  return item.isStrict !== false
    ? route.path === item.to
    : route.path.startsWith(item.to)
}

const allItems = computed<NavLink[]>(() => [
  ...props.items,
  ...(props.docsItems ?? []),
])

const activeItem = computed(() =>
  allItems.value.find(item => isActive(item)) ?? allItems.value[0],
)

watch(() => route.path, () => {
  isOpen.value = false
})

function mobileLinkClass(item: NavLink) {
  return [
    'flex items-center gap-2 px-4 py-3 text-xs font-semibold border-b border-default last:border-b-0',
    isActive(item) ? 'text-accent bg-neutral-200' : 'text-secondary',
    item.disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : '',
  ]
}
</script>

<template>
  <!-- Desktop: horizontal tabs using ui-kit Tabs primitives -->
  <div class="hidden md:block w-full min-w-0">
    <nav
      class="mx-auto max-w-[1200px] w-full min-w-0 px-5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      aria-label="Leaderboard Navigation"
    >
      <Tabs :model-value="activeItem?.to ?? route.path">
        <TabsList class="w-max min-w-full flex-nowrap gap-6 h-[52px]">
          <TabsTriggerLink
            v-for="(item, key) in allItems"
            :key="item.to ?? item.label ?? key"
            :to="item.disabled ? '' : (item.to ?? '')"
            :disabled="item.disabled"
            :aria-current="isActive(item) ? 'page' : undefined"
            class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap"
          >
            <span class="flex shrink-0 items-center justify-center">
              <component
                :is="navIcon(item)"
                v-if="navIcon(item)"
                class="size-[14px]"
                aria-hidden="true"
              />
            </span>
            {{ item.label }}
          </TabsTriggerLink>
          <TabsIndicator class="h-0.5" />
        </TabsList>
      </Tabs>
    </nav>
  </div>

  <!-- Mobile: sticky dropdown below PrMobileHeader -->
  <div
    class="md:hidden sticky top-[60px] z-[40] w-full bg-neutral-200"
  >
    <nav aria-label="Leaderboard Navigation">
      <Collapsible v-model:open="isOpen">
        <CollapsibleTrigger
          class="flex h-11 w-full items-center justify-between border-b border-default bg-neutral-200 px-4"
          aria-controls="leaderboard-nav-mobile-menu"
        >
          <span class="truncate text-xs font-normal text-secondary">
            {{ activeItem?.label }}
          </span>
          <component
            :is="isOpen ? ChevronUpIcon : ChevronDownIcon"
            class="size-5 shrink-0 text-secondary"
            aria-hidden="true"
          />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <ul
            id="leaderboard-nav-mobile-menu"
            class="border-b border-default bg-neutral-200"
          >
            <li
              v-for="(item, key) in allItems"
              :key="item.to ?? item.label ?? key"
            >
              <component
                :is="item.disabled || !item.to ? 'span' : NuxtLink"
                :to="item.disabled ? undefined : item.to"
                :class="mobileLinkClass(item)"
                :aria-current="isActive(item) ? 'page' : undefined"
                @click="isOpen = false"
              >
                <component
                  :is="navIcon(item)"
                  v-if="navIcon(item)"
                  class="size-[14px] shrink-0"
                  aria-hidden="true"
                />
                {{ item.label }}
              </component>
            </li>
          </ul>
        </CollapsibleContent>
      </Collapsible>
    </nav>
  </div>
</template>
